import { useState, useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';

export default function WithholdingCalculator() {
  useEffect(() => {
    document.title = "Tax Withholding Calculator 2025 | W-4 Form Helper - ItsJustTax";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Calculate correct tax withholding for your W-4 form. Estimate federal income tax withholding from your paycheck to avoid owing taxes or getting a large refund. Optimize your W-4 allowances for 2025.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'withholding calculator, W-4 calculator, tax withholding, paycheck tax calculator, W-4 form help');
    }
  }, []);

  const [annualSalary, setAnnualSalary] = useState('');
  const [payFrequency, setPayFrequency] = useState('biweekly');
  const [filingStatus, setFilingStatus] = useState('single');
  const [dependents, setDependents] = useState('0');
  const [otherIncome, setOtherIncome] = useState('');
  const [deductions, setDeductions] = useState('');
  const [extraWithholding, setExtraWithholding] = useState('');
  const [result, setResult] = useState<any>(null);
  const [scrolled, setScrolled] = useState(false);

  const payPeriods: Record<string, number> = {
    weekly: 52,
    biweekly: 26,
    semimonthly: 24,
    monthly: 12
  };

  const calculate = () => {
    const salary = parseFloat(annualSalary);
    const additional = parseFloat(otherIncome) || 0;
    const itemizedDeductions = parseFloat(deductions) || 0;
    const extra = parseFloat(extraWithholding) || 0;
    const numDependents = parseInt(dependents);

    if (isNaN(salary) || salary <= 0) {
      alert('Please enter a valid salary amount');
      return;
    }

    const totalIncome = salary + additional;
    const standardDeduction = filingStatus === 'single' ? 14600 : filingStatus === 'married' ? 29200 : 21900;
    const actualDeduction = Math.max(standardDeduction, itemizedDeductions);
    const taxableIncome = Math.max(0, totalIncome - actualDeduction);

    let annualTax = 0;
    if (filingStatus === 'single') {
      if (taxableIncome <= 11600) annualTax = taxableIncome * 0.10;
      else if (taxableIncome <= 47150) annualTax = 1160 + (taxableIncome - 11600) * 0.12;
      else if (taxableIncome <= 100525) annualTax = 5426 + (taxableIncome - 47150) * 0.22;
      else if (taxableIncome <= 191950) annualTax = 17168.50 + (taxableIncome - 100525) * 0.24;
      else if (taxableIncome <= 243725) annualTax = 39110.50 + (taxableIncome - 191950) * 0.32;
      else if (taxableIncome <= 609350) annualTax = 55678.50 + (taxableIncome - 243725) * 0.35;
      else annualTax = 183647.50 + (taxableIncome - 609350) * 0.37;
    } else if (filingStatus === 'married') {
      if (taxableIncome <= 23200) annualTax = taxableIncome * 0.10;
      else if (taxableIncome <= 94300) annualTax = 2320 + (taxableIncome - 23200) * 0.12;
      else if (taxableIncome <= 201050) annualTax = 10852 + (taxableIncome - 94300) * 0.22;
      else if (taxableIncome <= 383900) annualTax = 34337 + (taxableIncome - 201050) * 0.24;
      else if (taxableIncome <= 487450) annualTax = 78221 + (taxableIncome - 383900) * 0.32;
      else if (taxableIncome <= 731200) annualTax = 111357 + (taxableIncome - 487450) * 0.35;
      else annualTax = 196669.50 + (taxableIncome - 731200) * 0.37;
    } else {
      if (taxableIncome <= 16550) annualTax = taxableIncome * 0.10;
      else if (taxableIncome <= 63100) annualTax = 1655 + (taxableIncome - 16550) * 0.12;
      else if (taxableIncome <= 100500) annualTax = 7241 + (taxableIncome - 63100) * 0.22;
      else if (taxableIncome <= 191950) annualTax = 15469 + (taxableIncome - 100500) * 0.24;
      else if (taxableIncome <= 243700) annualTax = 37417 + (taxableIncome - 191950) * 0.32;
      else if (taxableIncome <= 609350) annualTax = 53977 + (taxableIncome - 243700) * 0.35;
      else annualTax = 181954.50 + (taxableIncome - 609350) * 0.37;
    }

    const childCredit = numDependents * 2000;
    const netTax = Math.max(0, annualTax - childCredit);
    
    const periods = payPeriods[payFrequency];
    const perPaycheckWithholding = (netTax / periods) + extra;
    const totalAnnualWithholding = perPaycheckWithholding * periods;

    setResult({
      annualSalary: salary,
      totalIncome,
      taxableIncome,
      annualTax,
      childCredit,
      netTax,
      perPaycheckWithholding,
      totalAnnualWithholding,
      payFrequency,
      periods,
      extraWithholding: extra
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-8">
            <a href="/" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 mb-4 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </a>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Withholding Calculator</h1>
            <p className="text-lg text-gray-600">Determine the right amount to withhold from your paycheck</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Employment Information</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Annual Salary</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={annualSalary}
                      onChange={(e) => setAnnualSalary(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="75000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Pay Frequency</label>
                  <select
                    value={payFrequency}
                    onChange={(e) => setPayFrequency(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg cursor-pointer"
                  >
                    <option value="weekly">Weekly (52 paychecks)</option>
                    <option value="biweekly">Bi-weekly (26 paychecks)</option>
                    <option value="semimonthly">Semi-monthly (24 paychecks)</option>
                    <option value="monthly">Monthly (12 paychecks)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Filing Status</label>
                  <select
                    value={filingStatus}
                    onChange={(e) => setFilingStatus(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg cursor-pointer"
                  >
                    <option value="single">Single</option>
                    <option value="married">Married Filing Jointly</option>
                    <option value="head">Head of Household</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Dependents</label>
                  <input
                    type="number"
                    value={dependents}
                    onChange={(e) => setDependents(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                    placeholder="0"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Other Annual Income</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={otherIncome}
                      onChange={(e) => setOtherIncome(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="0"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Interest, dividends, side income</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Itemized Deductions</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={deductions}
                      onChange={(e) => setDeductions(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="0"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Leave blank to use standard deduction</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Extra Withholding Per Paycheck</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={extraWithholding}
                      onChange={(e) => setExtraWithholding(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="0"
                    />
                  </div>
                </div>

                <button
                  onClick={calculate}
                  className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-teal-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Calculate Withholding
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 shadow-lg text-white">
              <h2 className="text-2xl font-bold mb-6">Withholding Results</h2>
              
              {result ? (
                <div className="space-y-6">
                  <div className="bg-white/10 rounded-xl p-6">
                    <div className="text-sm text-teal-100 mb-1">Withhold Per Paycheck</div>
                    <div className="text-4xl font-bold">${result.perPaycheckWithholding.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                    <div className="text-sm text-teal-100 mt-2">
                      {result.periods} {result.payFrequency} paychecks per year
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Annual Salary</span>
                      <span className="font-semibold">${result.annualSalary.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Total Income</span>
                      <span className="font-semibold">${result.totalIncome.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Taxable Income</span>
                      <span className="font-semibold">${result.taxableIncome.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Annual Tax</span>
                      <span className="font-semibold">${result.annualTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Child Tax Credits</span>
                      <span className="font-semibold">-${result.childCredit.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Net Tax</span>
                      <span className="font-semibold">${result.netTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                  </div>

                  <div className="bg-white/10 rounded-xl p-6">
                    <h3 className="font-semibold mb-3">Annual Summary</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-teal-100">Total Withholding</span>
                        <span>${result.totalAnnualWithholding.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                      </div>
                      {result.extraWithholding > 0 && (
                        <div className="flex justify-between">
                          <span className="text-teal-100">Extra Withholding</span>
                          <span>${(result.extraWithholding * result.periods).toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-yellow-500/20 rounded-xl p-4 border-2 border-yellow-400/30">
                    <p className="text-yellow-100 text-sm leading-relaxed">
                      <strong>Update Your W-4:</strong> Use these calculations to complete a new Form W-4 
                      with your employer to adjust your withholding.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center text-teal-100">
                    <i className="ri-file-list-3-line text-6xl mb-4 opacity-50"></i>
                    <p>Enter your employment information to calculate withholding</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">How to Update Your Withholding</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-teal-600">1</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Complete Form W-4</h4>
                <p className="text-gray-600 text-sm">Fill out a new W-4 form with your updated withholding information</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-teal-600">2</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Submit to Employer</h4>
                <p className="text-gray-600 text-sm">Give the completed form to your HR or payroll department</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-teal-600">3</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Verify Changes</h4>
                <p className="text-gray-600 text-sm">Check your next paycheck to confirm the new withholding amount</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}